/**
 * The one loud thing on the Pro home page: a single band above the panels.
 * Session mode leads with what's happening at the Capitol today; elections
 * mode leads with the countdown to the general. The right half previews the
 * next three calendar items in both modes.
 *
 * Deliberately not a filled color block — the band earns its priority from
 * type scale, the orange rule, and the pill. The sidebar stays the only dark
 * mass on screen.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import type { CalendarEvent } from '../../../types/calendar';
import type { ElectionInfo, HappeningNowData } from './useProHomeData';
import type { HomeMode } from './useHomeMode';
import { formatEventTime, formatRelativeDay, toDateOnly } from './homeFormat';
import { parseLocalDate, daysUntilLocalDate } from '../../../utils/dateUtils';
import { OpenExternalIcon } from '../../icons';

interface TodayBandProps {
  mode: HomeMode;
  todayEvents: CalendarEvent[];
  upcomingEvents: CalendarEvent[];
  happeningNow: HappeningNowData | null;
  electionInfo: ElectionInfo | null;
}

const EYEBROW_CLASS =
  'text-[11px] font-bold uppercase tracking-[.12em] text-primary-400 dark:text-primary-dark-400';

function UpcomingCards({ events, heading }: { events: CalendarEvent[]; heading: string }) {
  // Three is the fixed count: more than three → the next three; fewer → let
  // the row breathe. Never placeholder cards, never "None".
  const cards = events.slice(0, 3);
  return (
    <div className="flex-1 min-w-0">
      <div className={`${EYEBROW_CLASS} mb-2`}>{heading}</div>
      {cards.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-text-dark-secondary">
          Nothing on the calendar in the next two weeks.
        </p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3">
          {cards.map((event) => (
            <div
              key={event.calendar_id}
              className="flex-1 min-w-0 rounded-xl border border-border-light dark:border-border-dark-hairline bg-neutral-100 dark:bg-background-dark-tertiary px-3 py-2.5"
            >
              <div className="flex items-baseline gap-2">
                <span className="text-[13px] font-bold text-text-primary dark:text-text-dark-primary tabular-nums">
                  {formatEventTime(event)}
                </span>
                <span className="text-[9.5px] font-semibold uppercase tracking-wide text-slate-400 dark:text-text-dark-tertiary">
                  {formatRelativeDay(event.reading_date)}
                </span>
              </div>
              <div className="mt-1 text-[12.5px] font-semibold text-primary-800 dark:text-text-dark-primary truncate">
                {event.derived_location || event.chamber}
              </div>
              <div className="text-[11.5px] text-slate-500 dark:text-text-dark-secondary truncate">
                {event.reading_title}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SessionLead({
  todayEvents,
  upcomingEvents,
  happeningNow,
}: {
  todayEvents: CalendarEvent[];
  upcomingEvents: CalendarEvent[];
  happeningNow: HappeningNowData | null;
}) {
  const isLive =
    happeningNow?.houseFloor?.status === 'live' || happeningNow?.senateFloor?.status === 'live';
  const firstToday = todayEvents[0];
  const nextUp = firstToday ?? upcomingEvents[0];

  let headline: string;
  let sub: string;
  if (firstToday) {
    headline = firstToday.derived_location || `${firstToday.chamber} — ${firstToday.reading_title}`;
    const billCount = firstToday.bill_count ?? firstToday.bill_numbers?.length ?? 0;
    sub =
      billCount > 0
        ? `${firstToday.reading_title} · ${billCount} bill${billCount === 1 ? '' : 's'} on the agenda`
        : firstToday.reading_title;
  } else if (nextUp) {
    headline = `Next: ${nextUp.derived_location || nextUp.reading_title}`;
    sub = `${format(parseISO(toDateOnly(nextUp.reading_date)), 'EEEE, MMMM d')} · ${nextUp.reading_title}`;
  } else {
    headline = 'The legislature is adjourned';
    sub = 'No proceedings on the calendar in the next two weeks.';
  }

  const watchUrl = happeningNow?.liveVideoUrl || happeningNow?.watchListenUrl;

  return (
    <div className="flex gap-3">
      <div className="w-[3px] rounded-sm bg-action flex-none self-stretch" aria-hidden="true" />
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex items-center gap-2.5 flex-wrap">
          {isLive && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-party-republican-bg px-2 py-0.5 text-[10px] font-bold uppercase tracking-[.12em] text-party-republican">
              <span className="pro-home-live-dot w-1.5 h-1.5 rounded-full bg-error-600" aria-hidden="true" />
              Live
            </span>
          )}
          <span className={EYEBROW_CLASS}>Today at the Capitol</span>
        </div>
        <h2 className="font-display font-semibold text-2xl tracking-tight text-primary-800 dark:text-text-dark-primary">
          {headline}
        </h2>
        <p className="text-[13px] text-slate-500 dark:text-text-dark-secondary">{sub}</p>
        <div className="flex items-center gap-2 mt-1">
          {watchUrl && (
            <a
              href={watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-action px-3.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-action-hover"
            >
              Watch &amp; listen
              <OpenExternalIcon className="w-4 h-4" aria-hidden="true" />
            </a>
          )}
          <Link
            to="/calendar"
            className="inline-flex items-center rounded-lg border-[1.5px] border-border-light-strong dark:border-border-dark px-3.5 py-1.5 text-[12.5px] font-semibold text-primary-700 dark:text-text-dark-primary hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary"
          >
            Full calendar
          </Link>
        </div>
      </div>
    </div>
  );
}

function ElectionsLead({
  electionInfo,
  measureCount,
}: {
  electionInfo: ElectionInfo | null;
  measureCount: number | null;
}) {
  // parseLocalDate, not parseISO: the API serializes the DATE as UTC midnight,
  // which parseISO renders as the previous evening in Denver — the dashboard
  // showed "Monday, November 2" for a Tuesday-Nov-3 election (2026-08-26).
  const generalDate = electionInfo?.generalDate ? parseLocalDate(electionInfo.generalDate) : null;
  const daysOut = daysUntilLocalDate(electionInfo?.generalDate);

  return (
    <div className="flex gap-3">
      <div className="w-[3px] rounded-sm bg-action flex-none self-stretch" aria-hidden="true" />
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="inline-flex items-center rounded-full bg-primary-tint border border-primary-tint-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[.12em] text-primary-700">
            General election
          </span>
          {generalDate && (
            <span className={EYEBROW_CLASS}>{format(generalDate, 'EEEE, MMMM d, yyyy')}</span>
          )}
        </div>
        {daysOut !== null && daysOut >= 0 ? (
          <div className="flex items-baseline gap-2">
            <span className="font-display font-bold text-4xl tabular-nums text-primary-800 dark:text-text-dark-primary">
              {daysOut}
            </span>
            <span className="font-display font-semibold text-lg text-primary-800 dark:text-text-dark-primary">
              days out
            </span>
          </div>
        ) : (
          <h2 className="font-display font-semibold text-2xl tracking-tight text-primary-800 dark:text-text-dark-primary">
            {electionInfo?.name || 'Election season'}
          </h2>
        )}
        <p className="text-[13px] text-slate-500 dark:text-text-dark-secondary">
          {measureCount !== null && measureCount > 0
            ? `${measureCount} ballot measure${measureCount === 1 ? '' : 's'} on your board`
            : 'Track measures and candidates to see them here.'}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <Link
            to="/candidates"
            className="inline-flex items-center rounded-lg bg-action px-3.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-action-hover"
          >
            View candidates
          </Link>
          <Link
            to="/ballot-initiatives"
            className="inline-flex items-center rounded-lg border-[1.5px] border-border-light-strong dark:border-border-dark px-3.5 py-1.5 text-[12.5px] font-semibold text-primary-700 dark:text-text-dark-primary hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary"
          >
            Ballot measures
          </Link>
          {/* Resolved O5 (CALENDAR_SEPARATION_PRD): the elections lead links
              to the Election Calendar. */}
          <Link
            to="/elections/calendar"
            className="inline-flex items-center rounded-lg border-[1.5px] border-border-light-strong dark:border-border-dark px-3.5 py-1.5 text-[12.5px] font-semibold text-primary-700 dark:text-text-dark-primary hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary"
          >
            Election calendar
          </Link>
        </div>
      </div>
    </div>
  );
}

const TodayBand: React.FC<TodayBandProps & { measureCount: number | null }> = ({
  mode,
  todayEvents,
  upcomingEvents,
  happeningNow,
  electionInfo,
  measureCount,
}) => {
  const remainingToday = todayEvents.slice(1);
  const rightEvents = remainingToday.length > 0 ? remainingToday : upcomingEvents;
  const rightHeading =
    remainingToday.length > 0 ? 'Rest of today' : 'What’s next on the calendar';

  return (
    <section className="card flex-none p-5 flex flex-col lg:flex-row gap-5" aria-label="Today">
      <div className="lg:flex-none lg:w-[356px] lg:pr-5 lg:border-r border-border-light dark:border-border-dark-hairline">
        {mode === 'session' ? (
          <SessionLead
            todayEvents={todayEvents}
            upcomingEvents={upcomingEvents}
            happeningNow={happeningNow}
          />
        ) : (
          <ElectionsLead electionInfo={electionInfo} measureCount={measureCount} />
        )}
      </div>
      <UpcomingCards events={rightEvents} heading={rightHeading} />
    </section>
  );
};

export default TodayBand;
